import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Popover, PopoverBody } from 'reactstrap';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import * as icons from '../../assets';
import useNavdata from '../useNavdata';

function VerticalLayout({ layoutType }) {
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [stickyPopoverId, setStickyPopoverId] = useState(null);
  const popoverRef = useRef(null);
  const targetRef = useRef(null);
  const location = useLocation();
  const menus = useNavdata();
  const path = location.pathname;

  // toggle “sticky” popover on click
  const handleClick = id => {
    if (stickyPopoverId === id) {
      setStickyPopoverId(null);
      setOpenPopoverId(null);
    } else {
      setStickyPopoverId(id);
      setOpenPopoverId(id);
    }
  };

  // hover handlers only when no sticky popover
  const handleMouseEnter = id => {
    if (!stickyPopoverId) {
      setOpenPopoverId(id);
    }
  };
  const handleMouseLeave = () => {
    if (!stickyPopoverId) {
      setOpenPopoverId(null);
    }
  };

  // close all on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.nav-subitem-popover') &&
        !event.target.closest('.menu-link')
      ) {
        setOpenPopoverId(null);
        setStickyPopoverId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLayoutState = state => state.Layout;
  const selectLayoutProperties = createSelector(selectLayoutState, layout => ({
    leftsidbarSizeType: layout.leftsidbarSizeType,
    sidebarVisibilitytype: layout.sidebarVisibilitytype,
    layoutType: layout.layoutType,
  }));

  const { leftsidbarSizeType, sidebarVisibilitytype } = useSelector(
    selectLayoutProperties,
  );

  const resizeSidebarMenu = useCallback(() => {
    const windowSize = document.documentElement.clientWidth;
    const layout = document.documentElement.getAttribute('data-layout');
    const hamburgerIcon = document.querySelector('.hamburger-icon');
    if (windowSize >= 1025) {
      if (layout === 'vertical' || layout === 'semibox') {
        document.documentElement.setAttribute(
          'data-sidebar-size',
          leftsidbarSizeType,
        );
      }
      if (
        (sidebarVisibilitytype === 'show' ||
          layoutType === 'vertical' ||
          layoutType === 'twocolumn') &&
        hamburgerIcon
      ) {
        hamburgerIcon.classList.remove('open');
      } else if (hamburgerIcon) {
        hamburgerIcon.classList.add('open');
      }
    } else if (windowSize < 1025 && windowSize > 767) {
      document.body.classList.remove('twocolumn-panel');
      if (layout === 'vertical' || layout === 'semibox') {
        document.documentElement.setAttribute('data-sidebar-size', 'sm');
      }
      hamburgerIcon?.classList.add('open');
    } else if (windowSize <= 767) {
      document.body.classList.remove('vertical-sidebar-enable');
      if (layout !== 'horizontal') {
        document.documentElement.setAttribute('data-sidebar-size', 'sm');
      }
      hamburgerIcon?.classList.add('open');
    }
  }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType]);

  useEffect(() => {
    window.addEventListener('resize', resizeSidebarMenu, true);
    return () => window.removeEventListener('resize', resizeSidebarMenu);
  }, [resizeSidebarMenu]);

  const activateParentDropdown = item => {
    item.classList.add('active');
    const parentCollapseDiv = item.closest('.collapse.menu-dropdown');
    if (!parentCollapseDiv) return;
    parentCollapseDiv.classList.add('show');
    const parentLink = parentCollapseDiv.parentElement.children[0];
    parentLink.classList.add('active');
    parentLink.setAttribute('aria-expanded', 'true');
    let ancestor = parentCollapseDiv.parentElement.closest(
      '.collapse.menu-dropdown',
    );
    while (ancestor) {
      ancestor.classList.add('show');
      const link = ancestor.previousElementSibling;
      if (link) link.classList.add('active');
      ancestor = ancestor.parentElement.closest('.collapse.menu-dropdown');
    }
  };
  const removeActivation = items => {
    items
      .filter(x => x.classList.contains('active'))
      .forEach(item => {
        item.classList.remove('active');
        item.setAttribute('aria-expanded', 'false');
        item.nextElementSibling?.classList.remove('show');
      });
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const initMenu = () => {
      const pathName = process.env.PUBLIC_URL + path;
      const ul = document.getElementById('navbar-nav');
      const items = ul.getElementsByTagName('a');
      const itemsArray = [...items];
      removeActivation(itemsArray);
      const matchingMenuItem = itemsArray.find(x => x.pathname === pathName);
      if (matchingMenuItem) activateParentDropdown(matchingMenuItem);
    };
    if (layoutType === 'vertical') initMenu();
  }, [path, layoutType]);

  function renderBadge(name, color) {
    if (!name) return null;
    return (
      <span className={`badge badge-pill bg-${color}`} data-key="t-new">
        {name}
      </span>
    );
  }

  const renderLink = (
    id,
    to,
    label,
    badgeName,
    badgeColor,
    icon,
    click,
    isCollapse = false,
  ) => (
    <Link
      to={to || '/#'}
      className="nav-link menu-link"
      id={`popover-button-${id}`}
      innerRef={targetRef}
      title={label}
      onMouseEnter={() => isCollapse && handleMouseEnter(id)}
      onMouseLeave={() => isCollapse && handleMouseLeave(id)}
      onClick={e => {
        if (isCollapse) {
          e.preventDefault();
          handleClick(id);
        }
        if (click) click(e);
      }}
      data-bs-toggle={isCollapse ? 'collapse' : undefined}
    >
      {icon && (
        <img
          src={icons[icon]}
          alt="Sidemenu Icon"
          style={{ height: '20px', width: '20px' }}
        />
      )}
      <span className="ms-2">{label}</span>
      {renderBadge(badgeName, badgeColor)}
    </Link>
  );

  const renderSubLink = (
    id,
    to,
    label,
    badgeName,
    badgeColor,
    icon,
    click,
    isCollapse = false,
  ) => (
    <Link
      to={to || '/#'}
      className="nav-link menu-link"
      id={`popover-button-${id}`}
      onClick={e => {
        if (click) click(e);
        setOpenPopoverId(null);
        setStickyPopoverId(null);
      }}
      style={{ textWrap: 'nowrap' }}
      data-bs-toggle={isCollapse ? 'collapse' : undefined}
    >
      {icon && (
        <img
          src={icons[icon]}
          alt="Sidemenu Icon"
          style={{ height: '20px', width: '20px' }}
        />
      )}
      <span className="ms-2" style={{ fontSize: '13px' }}>
        {label}
      </span>
      {renderBadge(badgeName, badgeColor)}
    </Link>
  );

  return menus?.map(item => {
    if (item.subItems) {
      return (
        <li className="nav-item" key={`item-${item.label}`}>
          {renderLink(
            item.id,
            item.link,
            item.label,
            item.badgeName,
            item.badgeColor,
            item.icon,
            item.click,
            true,
          )}
          <Popover
            isOpen={openPopoverId === item.id}
            target={`popover-button-${item.id}`}
            innerRef={popoverRef}
            trigger="manual"
            fade={false}
            className="nav-subitem-popover"
          >
            <PopoverBody
              className="nav-subitem-popover-body"
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={() => handleMouseLeave(item.id)}
            >
              {item.subItems.map(subItem => (
                <Button className="sub-item" key={`sub-${subItem.link}`}>
                  {renderSubLink(
                    subItem.id,
                    subItem.link,
                    subItem.label,
                    subItem.badgeName,
                    subItem.badgeColor,
                    null,
                    subItem.click,
                    !!subItem.isChildItem,
                  )}
                </Button>
              ))}
            </PopoverBody>
          </Popover>
        </li>
      );
    }

    return (
      <li className="nav-item" key={`item-${item.link}`}>
        {renderLink(
          item.id,
          item.link,
          item.label,
          item.badgeName,
          item.badgeColor,
          item.icon,
          null,
          false,
        )}
      </li>
    );
  });
}

export default VerticalLayout;
